import React, { useEffect, useState, useCallback, useRef } from 'react';
import { firestore, storage } from '../Firebase';
import toast, { Toaster } from 'react-hot-toast';
import { Col, Container, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import HeaderAdmin from './HeaderAdmin';
import { useParams } from 'react-router-dom';
import DropzoneManage from './DropzoneManage';
import { BeatLoader } from 'react-spinners';
import { uploadImageToStorage } from '../utils/storageUpload';
import ChatAdmin from './ChatAdmin';
import QuestionBoxAdmin from './QuestionBoxAdmin';
import DynamicBoxOutput from './DynamicBoxOutput';
import DynamicBoxEditor from './DynamicBoxEditor';
import './DynamicBox.css';

const Manage = () => {
  const { videoID } = useParams();
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const prevVideoData = useRef(null); // Ref to track previous videoData
  const [dynamicBox, setDynamicBox] = useState('');
  const [selectedDynamicBoxSlideId, setSelectedDynamicBoxSlideId] =
    useState('');
  const [isDynamicBoxPreviewActive, setIsDynamicBoxPreviewActive] =
    useState(false);
  const [features, setFeatures] = useState({});

  useEffect(() => {
    const unsubscribe = firestore
      .collection('settings')
      .doc('features')
      .onSnapshot((doc) => {
        if (doc.exists) {
          setFeatures(doc.data());
        }
      });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        // Use get() instead of onSnapshot for initial load
        const doc = await firestore.collection('videos').doc(videoID).get();
        if (doc.exists) {
          const data = doc.data();
          // Set a default vimeoType if it doesn't exist
          if (!data.vimeoType) {
            data.vimeoType = 'upload'; // or 'event', whichever you prefer as default
          }
          setVideoData(data);
          prevVideoData.current = data;
        } else {
          toast.error('No such video found.');
        }
      } catch (error) {
        toast.error('Error fetching video.');
      } finally {
        setLoading(false);
      }
    };

    if (videoID) {
      fetchVideo();
    }

    // Only set up a listener if you need real-time updates
    // Otherwise, just use the fetchVideo function above
    const unsubscribe = firestore
      .collection('videos')
      .doc(videoID)
      .onSnapshot(
        (doc) => {
          if (doc.exists) {
            const data = doc.data();
            if (!data.vimeoType) {
              data.vimeoType = 'upload';
            }
            setVideoData(data);
            prevVideoData.current = data;
          }
        },
        (error) => {
          console.error('Error in video listener:', error);
        }
      );

    return () => unsubscribe();
  }, [videoID]);

  const [localVideoData, setLocalVideoData] = useState(null);

  useEffect(() => {
    if (videoData) {
      setLocalVideoData(videoData);
    }
  }, [videoData]);

  useEffect(() => {
    // Only set dynamicBox on initial load or when a publish has happened
    // Check if activeSlideId changed
    if (
      videoData &&
      ((videoData.dynamicBox && dynamicBox === '') ||
        prevVideoData.current?.activeSlideId !== videoData.activeSlideId)
    ) {
      setDynamicBox(videoData.dynamicBox || '');
    }
  }, [videoData, dynamicBox]);

  const updateVideos = useCallback(
    (dataToUpdate) => {
      const updatedData = { ...dataToUpdate };
      firestore
        .collection('videos')
        .doc(videoID)
        .set(updatedData, { merge: true })
        .then(() => {
          toast.success('Settings updated successfully!');
          prevVideoData.current = updatedData;
        })
        .catch((error) => {
          toast.error('Error updating settings: ' + error.message);
        });
    },
    [videoID]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLocalVideoData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSetClick = (field) => {
    if (localVideoData[field] !== videoData[field]) {
      updateVideos({ [field]: localVideoData[field] });
    }
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setLocalVideoData((prevState) => {
      const newState = {
        ...prevState,
        [name]: value,
      };
      // Call updateVideos with ONLY the changed field
      setTimeout(() => updateVideos({ [name]: value }), 0);
      return newState;
    });
  };

  const handleCheckboxChange = (e) => {
    const newValue = e.target.checked ? 1 : 0;
    setLocalVideoData((prevState) => ({
      ...prevState,
      chatVisible: newValue,
    }));
    updateVideos({ chatVisible: newValue });
  };

  const handleDynamicBoxChange = (newContent, slideMeta = {}) => {
    setDynamicBox(newContent || '');
    setSelectedDynamicBoxSlideId(slideMeta.slideId || '');
    setIsDynamicBoxPreviewActive(Boolean(slideMeta.isActive));
  };

  if (!loading && !videoData) {
    return <div className="text-center mt-5">Video not found.</div>;
  }

  const vimeoSrc =
    videoData && videoData.vimeoID && videoData.vimeoKey
      ? videoData.vimeoType === 'upload'
        ? `https://player.vimeo.com/video/${videoData.vimeoID}?h=${videoData.vimeoKey}&badge=0&autopause=0&player_id=0&app_id=58479`
        : `https://vimeo.com/event/${videoData.vimeoID}/embed/${videoData.vimeoKey}`
      : '';

  const onDropHandler = async ({ name, uri }) => {
    if (!name || !uri) return;

    setLoading(true);
    try {
      const downloadURL = await uploadImageToStorage(storage, uri);
      const videoRef = firestore.collection('videos').doc(videoID);
      await videoRef.set({ thumbnail: downloadURL }, { merge: true });

      setVideoData((prevData) => ({
        ...prevData,
        thumbnail: downloadURL,
      }));

      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(
        'An error occurred while uploading the file. Please try again.'
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid>
      <Row>
        <HeaderAdmin />
        <Toaster />
        {loading && (
          <div className="overlay">
            <BeatLoader
              color="#36d7b7"
              size={15}
              loading={loading}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          </div>
        )}
      </Row>
      <Row>
        <Col>
          <div className="h1">
            {localVideoData?.title || ''} <span className="h5">Manage</span>
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={8}>
          {localVideoData && (
            <>
              {vimeoSrc ? (
                <div className="video-container mb-4">
                  <iframe
                    title="vimeoVideo"
                    src={vimeoSrc}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <div className="alert alert-warning mb-4" role="alert">
                  Please enter the Vimeo ID and Key to see the video preview.
                </div>
              )}
              {features.hasDynamicBox && (
                <div className="dynamic-box-container p-2 bg-white rounded-3 shadow w-100 mb-3">
                  <h5 className="mb-3">Dynamic Box</h5>
                  <Row>
                    <Col lg={7} className="mb-3 mb-lg-0">
                      <div className="dynamic-box-panel h-100">
                        <DynamicBoxEditor
                          initialContent={videoData?.dynamicBox || ''}
                          onContentChange={handleDynamicBoxChange}
                          videoID={videoID}
                        />
                      </div>
                    </Col>
                    <Col lg={5}>
                      <div
                        className={`dynamic-box-panel h-100 output-section ${
                          isDynamicBoxPreviewActive
                            ? 'dynamic-box-preview-panel'
                            : ''
                        }`}
                      >
                        <h3>
                          Preview
                          {isDynamicBoxPreviewActive && (
                            <span className="dynamic-box-active-preview-label">
                              Active
                            </span>
                          )}
                        </h3>
                        <DynamicBoxOutput
                          key={selectedDynamicBoxSlideId || 'unsaved'}
                          content={dynamicBox}
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              )}
            </>
          )}
        </Col>
        <Col md={4}>
          {features.hasQuestionBox && (
            <Row>
              <Col className="p-2 bg-white rounded-3 shadow w-100 mb-3">
                <QuestionBoxAdmin />
              </Col>
            </Row>
          )}
          {features.hasChat && (
            <Row>
              <Col className="p-2 bg-white rounded-3 shadow w-100 mb-3">
                <ChatAdmin />
              </Col>
            </Row>
          )}
          <Row>
            <Col className="p-2 bg-white rounded-3 shadow w-100 mb-3">
              <OverlayTrigger
                placement={'left'}
                overlay={<Tooltip>Title of the video.</Tooltip>}
              >
                <div className="input-group mb-3">
                  <div className="form-floating form-floating-group flex-grow-1">
                    <input
                      id="video-title"
                      className="form-control conf-option-field"
                      name="title"
                      type="text"
                      value={localVideoData?.title || ''}
                      onChange={handleInputChange}
                    ></input>
                    <label
                      className="form-label text-start"
                      htmlFor="video-title"
                    >
                      Title
                    </label>
                  </div>
                  <button
                    className="input-group-text set-btn btn btn-primary"
                    onClick={() => handleSetClick('title')}
                  >
                    Set
                  </button>
                </div>
              </OverlayTrigger>

              <div className="mb-3">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    name="hasLightSubtitle"
                    type="checkbox"
                    checked={localVideoData?.chatVisible || false}
                    onChange={handleCheckboxChange}
                    id="chatVisible"
                  ></input>
                  <OverlayTrigger
                    placement={'left'}
                    overlay={
                      <Tooltip>
                        When Chat is Visble, it makes the chat window go away. The
                        chat input box will still work.
                      </Tooltip>
                    }
                  >
                    <label className="form-check-label" htmlFor="chatVisible">
                      Show Chat Messages
                    </label>
                  </OverlayTrigger>
                </div>
              </div>

              <OverlayTrigger
                placement={'left'}
                overlay={
                  <Tooltip>
                    Only applies when Conference Mode is off. Vimeo Event id is
                    7 characters, is used for live streams. Vimeo Upload id is 9
                    characters, is used for on-demand videos.
                  </Tooltip>
                }
              >
                <div className="mb-3">
                  <div>Vimeo Type:</div>
                  <div className="d-flex align-items-center">
                    <div className="form-check form-check-inline mb-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="vimeoType"
                        id="vimeo-event"
                        value="event"
                        onChange={handleRadioChange}
                        checked={localVideoData?.vimeoType === 'event'}
                      />
                      <label className="form-check-label" htmlFor="vimeo-event">
                        Event
                      </label>
                    </div>
                    <div className="form-check form-check-inline mb-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="vimeoType"
                        id="vimeo-upload"
                        value="upload"
                        onChange={handleRadioChange}
                        checked={localVideoData?.vimeoType === 'upload'}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="vimeo-upload"
                      >
                        Upload
                      </label>
                    </div>
                  </div>
                </div>
              </OverlayTrigger>
              <OverlayTrigger
                placement={'left'}
                overlay={
                  <Tooltip>
                    Vimeo Key only necessary if Vimeo Event is set to private.
                  </Tooltip>
                }
              >
                <div className="input-group mb-3">
                  <div className="form-floating form-floating-group flex-grow-1">
                    <input
                      id="vimeo-id"
                      className="form-control conf-option-field"
                      name="vimeoID"
                      type="text"
                      value={localVideoData?.vimeoID || ''}
                      onChange={handleInputChange}
                    ></input>
                    <label className="form-label text-start" htmlFor="vimeo-id">
                      Vimeo ID
                    </label>
                  </div>
                  <div className="form-floating form-floating-group flex-grow-1">
                    <input
                      id="vimeo-key"
                      className="form-control conf-option-field"
                      name="vimeoKey"
                      type="text"
                      value={localVideoData?.vimeoKey || ''}
                      onChange={handleInputChange}
                    ></input>
                    <label
                      className="form-label text-start"
                      htmlFor="vimeo-key"
                    >
                      Vimeo Key
                    </label>
                  </div>
                  <button
                    className="input-group-text set-btn btn btn-primary"
                    onClick={() => {
                      handleSetClick('vimeoID');
                      handleSetClick('vimeoKey');
                    }}
                  >
                    Set
                  </button>
                </div>
              </OverlayTrigger>

              <OverlayTrigger
                placement={'left'}
                overlay={
                  <Tooltip>
                    This message pops up above the player when you change it. If
                    it is set to empty, the banner will disappear.
                  </Tooltip>
                }
              >
                <div className="input-group mb-3">
                  <div className="form-floating form-floating-group flex-grow-1">
                    <input
                      className="form-control conf-option-field"
                      name="bannerMessage"
                      type="text"
                      value={localVideoData?.bannerMessage || ''}
                      onChange={handleInputChange}
                    ></input>
                    <label
                      className="form-label text-start"
                      htmlFor="video-bannerMessage"
                    >
                      Banner Message
                    </label>
                  </div>
                  <button
                    className="input-group-text set-btn btn btn-primary"
                    onClick={() => handleSetClick('bannerMessage')}
                  >
                    Set
                  </button>
                  <button
                    className="input-group-text set-btn btn btn-danger"
                    onClick={() => {
                      setLocalVideoData((prevState) => ({
                        ...prevState,
                        bannerMessage: '',
                      }));
                      updateVideos({ bannerMessage: '' });
                    }}
                  >
                    Clear
                  </button>
                </div>
              </OverlayTrigger>

              <OverlayTrigger
                placement={'left'}
                overlay={
                  <Tooltip>
                    Update your thumbnail image by dragging and dropping.
                  </Tooltip>
                }
              >
                <div>
                  <DropzoneManage
                    onDropHandler={onDropHandler}
                    imagePath={videoData?.thumbnail || ''}
                  />
                </div>
              </OverlayTrigger>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default Manage;
